export interface DelegatedActionInput {
  actionId?: string;
  /** Bounded discriminator lifted from the control's element-key data attribute. */
  elementKey?: string;
  tagName: string;
  eventType: 'click' | 'change';
  disabled: boolean;
  ariaDisabled: boolean;
  /** A read-only control (e.g. a revealed quiz answer) changes nothing when activated. */
  ariaReadOnly?: boolean;
  /** The control owns exact semantic capture and only exposes the DOM marker for autocapture. */
  manualCapture?: boolean;
  mouseDetail?: number;
  changeMethod?: 'keyboard' | 'pointer';
}

export interface DelegatedActionCapture {
  actionId: string;
  elementKey?: string;
  method: 'keyboard' | 'pointer';
}

const formControls = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

/** Browser delegation policy shared by web surfaces and unit-tested without a DOM. */
export function resolveDelegatedAction(
  input: DelegatedActionInput,
): DelegatedActionCapture | undefined {
  if (
    !input.actionId ||
    input.disabled ||
    input.ariaDisabled ||
    input.ariaReadOnly ||
    input.manualCapture
  )
    return undefined;
  const tagName = input.tagName.toUpperCase();
  const formControl = formControls.has(tagName);

  const elementKey = input.elementKey ? { elementKey: input.elementKey } : {};
  if (input.eventType === 'change') {
    return formControl
      ? { actionId: input.actionId, ...elementKey, method: input.changeMethod ?? 'keyboard' }
      : undefined;
  }
  if (formControl) return undefined;
  return {
    actionId: input.actionId,
    ...elementKey,
    method: input.mouseDetail === 0 ? 'keyboard' : 'pointer',
  };
}
